<template>
  <div
    v-if="is_initialized"
    class="flex flex-col gap-6"
  >
    <slot
      :is-visible="is_edit"
      name="customer-form-header"
      :reset="setInitial"
      :submit="validate"
    />

    <section class="flex flex-col">
      <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
        <TitleForm>
          {{ $t('contact.labels.general') }}
        </TitleForm>
        <slot name="general-actions" />
      </div>
      <div
        v-if="show_general_tags_row"
        class="mb-5 flex flex-wrap items-center gap-2"
      >
        <slot name="customer-form-general-tags">
          <ContactStatus
            v-if="customer.id"
            :contact="customer"
            :is_show_timestamp="true"
          />
        </slot>
      </div>
      <b-field grouped>
        <SelectContactTitle
          :disabled="disabled"
          :is-expanded="false"
          :is_read_only="isReadOnly('title')"
          :title_prop="customer.attributes.title"
          @change="is_edit = true"
          @input="(title) => (customer.attributes.title = title)"
        />
        <b-field
          expanded
          :label="`${$t('customer.attributes.first_name')} *`"
          labelFor="first-name"
        >
          <div class="field">
            <input
              id="first-name"
              v-model.trim="v$.customer.attributes.first_name.$model"
              class="input"
              :class="{ 'is-danger': v$.customer.attributes.first_name.$error, 'is-static': isReadOnly('first_name') }"
              data-test="customer-first-name"
              :disabled="disabled"
              :readonly="isReadOnly('first_name')"
              type="text"
              @keyup="is_edit = true"
            />
            <div v-if="v$.customer.attributes.first_name.$error">
              <p
                v-if="v$.customer.attributes.first_name.required.$invalid"
                class="help is-danger"
              >
                {{ $t('customer.validation.needFirstName') }}
              </p>
            </div>
          </div>
        </b-field>

        <b-field
          expanded
          :label="`${$t('customer.attributes.last_name')} *`"
          labelFor="last-name"
        >
          <div class="field">
            <input
              id="last-name"
              v-model.trim="v$.customer.attributes.last_name.$model"
              class="input"
              :class="{ 'is-danger': v$.customer.attributes.last_name.$error, 'is-static': isReadOnly('last_name') }"
              data-test="customer-last-name"
              :disabled="disabled"
              :readonly="isReadOnly('last_name')"
              required
              type="text"
              @keyup="is_edit = true"
            />
            <div v-if="v$.customer.attributes.last_name.$error">
              <p
                v-if="v$.customer.attributes.last_name.required.$invalid"
                class="help is-danger"
              >
                {{ $t('customer.validation.needLastName') }}
              </p>
            </div>
          </div>
        </b-field>
      </b-field>

      <b-field
        :label="`${$t('customer.attributes.email_address')}`"
        labelFor="email-address"
      >
        <div v-if="props.action === 'create'">
          <input
            id="email-address"
            class="input grow"
            :class="{ 'is-danger': v$.customer.attributes.email_address.$error }"
            data-test="customer-email-address"
            :disabled="disabled"
            required
            @input="debouncedEmailHandler"
          />
          <div
            v-if="v$.customer.attributes.email_address.$error"
            data-test="email-validation-errors"
          >
            <p
              v-if="v$.customer.attributes.email_address.email.$invalid"
              class="help is-danger"
            >
              {{ $t('profile.validation.mustBeEmailAddress') }}
            </p>
            <p
              v-if="v$.customer.attributes.email_address.emailAddressUniq.$invalid"
              class="help is-danger"
            >
              {{ $t('profile.validation.emailTaken') }}
            </p>
            <p
              v-if="v$.customer.attributes.email_address.emailAddressDemo.$invalid"
              class="help is-danger"
            >
              {{ $t('validation.emailAddressDemo', { email_address_suffix }) }}
            </p>
          </div>
        </div>
        <div
          v-else
          class="field is-inline-flex items-center"
        >
          <span>
            <span
              v-if="is_userless"
              :class="{ 'text-gray-500 italic': customer.attributes.email_address === null }"
            >
              {{ customer.attributes.email_address || $t('profile.labels.noEmailAddressAdded') }}
            </span>
            <a
              v-else-if="customer.attributes.email_address === null"
              class="text-gray-500 italic"
              @click="openUpdateEmailAddressModal"
            >
              {{ $t('profile.labels.noEmailAddressAdded') }} <FontAwesomeIcon :icon="['far', 'pen']" />
            </a>
            <a
              v-else-if="customer.attributes.profiles_count === 1"
              @click="openUpdateEmailAddressModal"
            >
              {{ customer.attributes.email_address }} <FontAwesomeIcon :icon="['far', 'pen']" />
            </a>
            <a
              v-else
              v-tooltip="$t('profile.labels.multipleProfiles')"
            >
              {{ customer.attributes.email_address }} <FontAwesomeIcon icon="circle-info" />
            </a>
          </span>
        </div>
      </b-field>
      <EmailAddressStatusIndicator
        :profile="customer"
        :show_description="true"
      />
      <b-field grouped>
        <b-field
          expanded
          :label="$t('customer.attributes.phone_mobile')"
          labelFor="phone_mobile"
        >
          <input
            id="phone_mobile"
            v-model.trim="customer.attributes.phone_mobile"
            class="input"
            :class="{ 'is-static': isReadOnly('phone_mobile') }"
            :disabled="disabled"
            :readonly="isReadOnly('phone_mobile')"
            @keyup="is_edit = true"
          />
        </b-field>

        <b-field
          expanded
          :label="$t('customer.attributes.phone_landline')"
          labelFor="phone_landline"
        >
          <input
            id="phone_landline"
            v-model.trim="customer.attributes.phone_landline"
            class="input"
            :class="{ 'is-static': isReadOnly('phone_landline') }"
            :disabled="disabled"
            :readonly="isReadOnly('phone_landline')"
            @keyup="is_edit = true"
          />
        </b-field>

        <b-field
          expanded
          :label="$t('customer.attributes.iban')"
          labelFor="iban"
        >
          <input
            id="iban"
            v-model.trim="customer.attributes.iban"
            class="input"
            :class="{ 'is-static': isReadOnly('iban') }"
            :disabled="disabled"
            :readonly="isReadOnly('iban')"
            @keyup="is_edit = true"
          />
        </b-field>
      </b-field>

      <BField :label="$t('customer.attributes.language')">
        <SelectLanguage
          :disabled="disabled"
          :is_customer="true"
          :is_read_only="isReadOnly('language')"
          :language_prop="customer.attributes.language"
          @change="is_edit = true"
          @input="(language) => (customer.attributes.language = language)"
        />
      </BField>

      <div class="field">
        <label class="label">{{ $t('customer.attributes.category') }}</label>
        <div v-if="!isReadOnly('category')">
          <b-radio
            v-for="category in Customer.CATEGORIES"
            :key="category"
            v-model="customer.attributes.category"
            name="name"
            :native-value="category"
            @change="is_edit = true"
          >
            {{ $t(`customer.attributes.categories.${category}`) }}
          </b-radio>
        </div>
        <div v-else>
          <p v-if="customer.attributes.category">
            {{ $t(`customer.attributes.categories.${customer.attributes.category}`) }}
          </p>
          <p v-else />
        </div>
      </div>
      <b-field grouped>
        <b-field
          expanded
          :label="$t('customer.attributes.birth_date')"
          labelFor="birth-date"
        >
          <SelectDate
            v-if="!isReadOnly('birth_date')"
            id="birth-date"
            :date_prop="customer.attributes.birth_date"
            :disabled="disabled"
            @setDate="
              (val) => {
                ((customer.attributes.birth_date = val), (is_edit = true));
              }
            "
          />
          <input
            v-else
            class="input is-static"
            readonly
            :value="formatWithLocale(customer.attributes.birth_date, DATE_FORMATS.DAY_MONTH_YEAR)"
          />
        </b-field>
      </b-field>
    </section>

    <section>
      <TitleForm class="mb-4">
        {{ $t('contact.labels.address') }}
      </TitleForm>
      <AddressForm
        v-model:address_city="customer.attributes.address_city"
        v-model:address_country="customer.attributes.address_country"
        v-model:address_number="customer.attributes.address_number"
        v-model:address_street="customer.attributes.address_street"
        v-model:address_zip="customer.attributes.address_zip"
        class="field"
        :disabled="disabled"
        :is_read_only="isReadOnly('address_street')"
        @edit="is_edit = true"
      />
    </section>

    <section v-if="add_company || has_company">
      <div class="mb-4 flex items-baseline">
        <TitleForm class="grow">
          {{ $t('contact.labels.company') }}
        </TitleForm>
        <a
          v-if="!disabled"
          class="text-sm italic"
          @click="removeCompany"
        >
          {{ $t('customer.actions.removeCompany') }}
        </a>
      </div>
      <b-field
        :label="$t('customer.attributes.company_name')"
        labelFor="company-name"
      >
        <input
          id="company-name"
          v-model.trim="customer.attributes.company_name"
          class="input"
          :disabled="disabled"
          type="text"
          @keyup="is_edit = true"
        />
      </b-field>

      <b-field
        :label="$t('customer.attributes.company_vat')"
        labelFor="company-vat"
      >
        <input
          id="company-vat"
          v-model.trim="customer.attributes.company_vat"
          class="input"
          :disabled="disabled"
          type="text"
          @keyup="is_edit = true"
        />
      </b-field>
      <AddressForm
        v-model:address_city="customer.attributes.company_address_city"
        v-model:address_country="customer.attributes.company_address_country"
        v-model:address_number="customer.attributes.company_address_number"
        v-model:address_street="customer.attributes.company_address_street"
        v-model:address_zip="customer.attributes.company_address_zip"
        class="field"
        :disabled="disabled"
        prefix="company"
        @edit="is_edit = true"
      />
    </section>

    <section v-if="!add_company && !has_company && !disabled">
      <ButtonSecondary
        :disabled="disabled"
        @onClick="add_company = true"
      >
        {{ $t('customer.actions.addCompany') }}
      </ButtonSecondary>
    </section>

    <section>
      <TitleForm class="mb-4">
        {{ $t('customer.attributes.internal_note') }}
      </TitleForm>
      <EditorZiggu
        v-model:content_html="customer.attributes.internal_note_html"
        :content_key="customer.id"
        data-test="customer-internal-note"
        :is_textarea="true"
        @keyup="is_edit = true"
      />
    </section>

    <section>
      <SelectIntegration
        v-if="
          integration_sync_record(RECORD_TYPE.CUSTOMER) &&
          (customer.attributes.integration_id || customer.attributes.integration_oid)
        "
        v-model="customer"
        @update:modelValue="is_edit = true"
      />
    </section>

    <footer>
      <slot
        :is-visible="is_edit"
        name="customer-form-footer"
        :reset="setInitial"
        :submit="validate"
      />
    </footer>

    <PrimeDialog
      :visible="cannot_update_field_modal"
      modal
      :header="$t('profile.labels.changeEmailAddress', { profile_name: customer.attributes.name })"
      @update:visible="cannot_update_field_modal = false"
    >
      <p>{{ $t('profile.labels.maintainerCannotUpdateEmailAddress') }}</p>
      <template #footer>
        <ButtonPrimary @onClick="cannot_update_field_modal = false">
          {{ $t('modal.labels.gotItMessage') }}
        </ButtonPrimary>
      </template>
    </PrimeDialog>

    <PrimeDialog
      :visible="show_update_email_modal"
      modal
      :header="$t('profile.labels.changeEmailAddress', { profile_name: customer.attributes.name })"
      @update:visible="show_update_email_modal = false"
    >
      <UpdateEmailAddressModal
        ref="update_email_modal_ref"
        :profile="customer"
        :email_address_old="customer.attributes.email_address"
        @close="show_update_email_modal = false"
        @updateEmailAddress="updateEmailAddress"
      />
      <template #footer>
        <div class="flex justify-end gap-2">
          <ButtonTertiary @onClick="show_update_email_modal = false">
            {{ $t('modal.actions.cancel') }}
          </ButtonTertiary>
          <ButtonPrimary @onClick="update_email_modal_ref.submit()">
            {{ $t('profile.actions.changeEmail') }}
          </ButtonPrimary>
        </div>
      </template>
    </PrimeDialog>
  </div>
</template>

<script setup>
import { computed, ref, useSlots, watch } from 'vue';
import { useStore } from 'vuex';
import { helpers, required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import EmailAddressStatusIndicator from '@/app/_employees/components/users/EmailAddressStatusIndicator.vue';
import AddressForm from '@/app/shared_components/geo/AddressForm.vue';
import SelectLanguage from '@/app/shared_components/selects/SelectLanguage.vue';
import SelectDate from '@/app/shared_components/selects/SelectDate.vue';
import EditorZiggu from '@/app/shared_components/EditorZiggu.vue';
import UpdateEmailAddressModal from '@/app/_employees/components/contacts/TheUpdateEmailAddressModal.vue';
import SelectContactTitle from '@/app/shared_components/selects/SelectContactTitle.vue';
import { DATE_FORMATS, formatWithLocale } from '@/app/util/date_fns';
import { Customer, Employee, RECORD_TYPE } from '@/app/data/model_constants';
import { email } from '@/app/util/validators';
import useContactEmail from '@/app/util/use_contact_email';
import useUserlessProfile from '@/composables/useUserlessProfile';
import ContactStatus from '@/app/_employees/components/contacts/ContactStatus.vue';
import SelectIntegration from '@/app/shared_components/selects/SelectIntegration.vue';

defineOptions({ name: 'TheCustomerForm' });

const { withAsync } = helpers;

const props = defineProps({
  customer_prop: { type: Object, required: true },
  disabled: { type: Boolean },
  action: {
    type: String,
    validator: (val) => ['create', 'edit'].includes(val),
    required: true,
  },
});

const emit = defineEmits(['setCustomer']);

const store = useStore();
const { email_address_suffix, emailAddressDemo, isEmailUnique, createDebouncedEmailHandler } = useContactEmail();

const customer = ref(null);
const { is_userless } = useUserlessProfile(customer);
const is_edit = ref(false);
const is_initialized = ref(false);
const add_company = ref(false);
const cannot_update_field_modal = ref(false);
const show_update_email_modal = ref(false);
const update_email_modal_ref = ref(null);

const slots = useSlots();
const show_general_tags_row = computed(
  () => Boolean(slots['customer-form-general-tags']) || Boolean(_.get(customer.value, 'id')),
);

const current_client = computed(() => store.getters.current_client);
const current_profile = computed(() => store.getters.current_profile);
const integration_authorize_attribute = computed(() => store.getters.integration_authorize_attribute);
const integration_sync_record = computed(() => store.getters.integration_sync_record);

const has_company = computed(() => {
  const a = customer.value.attributes;
  return (
    a.company_name ||
    a.company_vat ||
    a.company_address_street ||
    a.company_address_number ||
    a.company_address_zip ||
    a.company_address_city ||
    a.company_address_country
  );
});

const rules = computed(() => ({
  customer: {
    attributes: {
      first_name: { required },
      last_name: { required },
      email_address: {
        email,
        emailAddressUniq: withAsync((email_address) => {
          if (props.action === 'edit' || _.isNil(email_address) || email_address === '') {
            return Promise.resolve(true);
          }
          return isEmailUnique(email_address.trim());
        }),
        emailAddressDemo,
      },
    },
  },
}));

const v$ = useVuelidate(rules, { customer });

function setInitial() {
  is_initialized.value = false;
  customer.value = _.cloneDeep(props.customer_prop);
  is_edit.value = false;
  add_company.value = false;
  is_initialized.value = true;
}

watch(
  () => props.customer_prop,
  (new_val, old_val) => {
    if (new_val && old_val && new_val.id !== old_val.id) setInitial();
  },
);
setInitial();

function isReadOnly(attribute) {
  return !integration_authorize_attribute.value(
    customer.value.attributes.integration_id,
    customer.value.type,
    attribute,
  );
}

function openUpdateEmailAddressModal() {
  if (current_profile.value.attributes.role !== Employee.ROLE_OWNER) {
    cannot_update_field_modal.value = true;
    return;
  }
  show_update_email_modal.value = true;
}

function updateEmailAddress(email_address_new) {
  customer.value.attributes.email_address = email_address_new;
  emit('setCustomer', customer.value);
  is_edit.value = false;
}

const debouncedEmailHandler = createDebouncedEmailHandler(() => v$.value.customer.attributes, customer);

function removeCompany() {
  if (has_company.value) is_edit.value = true;
  customer.value.attributes.company_name = '';
  customer.value.attributes.company_vat = '';
  customer.value.attributes.company_address_city = '';
  customer.value.attributes.company_address_number = '';
  customer.value.attributes.company_address_country = '';
  customer.value.attributes.company_address_zip = '';
  customer.value.attributes.company_address_street = '';
  add_company.value = false;
}

function validate() {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  emit('setCustomer', customer.value);
  is_edit.value = false;
}

defineExpose({ validate });
</script>
